import { supabase } from '../supabase';
import { Tables } from '@/types/database.types';
import { handleError } from './db-utils';
import { getProjectById } from './projects';

interface CreateRiskOptions {
  summary: string;
  description?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  status?: 'open' | 'mitigating' | 'mitigated' | 'closed';
  ownerId?: string;
  ownerName?: string | null;
  identifiedDate?: string | null;
  mitigationPlan?: string | null;
}

interface UpdateRiskOptions {
  summary?: string;
  description?: string | null;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  status?: 'open' | 'mitigating' | 'mitigated' | 'closed';
  ownerId?: string | null;
  ownerName?: string | null;
  identifiedDate?: string | null;
  mitigationPlan?: string | null;
}

/**
 * Get all risks for a project
 */
export const getProjectRisks = async (projectId: string): Promise<Tables<'risks'>[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check project access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('risks')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) handleError(error, 'fetch risks');
    return data || [];
  } catch (error) {
    handleError(error, 'fetch risks');
    return [];
  }
};

/**
 * Create a new risk for a project
 */
export const createRisk = async (
  projectId: string,
  options: CreateRiskOptions
): Promise<Tables<'risks'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check project access
    const project = await getProjectById(projectId);
    if (!project) throw new Error('Project not found');

    const { data, error } = await supabase
      .from('risks')
      .insert({
        project_id: projectId,
        summary: options.summary,
        description: options.description || null,
        severity: options.severity || 'medium',
        status: options.status || 'open',
        owner_id: options.ownerId || null,
        owner_name: options.ownerName || null,
        identified_date: options.identifiedDate || null,
        mitigation_plan: options.mitigationPlan || null,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) handleError(error, 'create risk');
    if (!data) throw new Error('No data returned from create');
    return data;
  } catch (error) {
    handleError(error, 'create risk');
    throw error;
  }
};

/**
 * Update a risk
 */
export const updateRisk = async (
  riskId: string,
  options: UpdateRiskOptions
): Promise<Tables<'risks'>> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (options.summary !== undefined) updateData.summary = options.summary;
    if (options.description !== undefined) updateData.description = options.description;
    if (options.severity !== undefined) updateData.severity = options.severity;
    if (options.status !== undefined) updateData.status = options.status;
    if (options.ownerId !== undefined) updateData.owner_id = options.ownerId;
    if (options.ownerName !== undefined) updateData.owner_name = options.ownerName;
    if (options.identifiedDate !== undefined) updateData.identified_date = options.identifiedDate;
    if (options.mitigationPlan !== undefined) updateData.mitigation_plan = options.mitigationPlan;

    const { data, error } = await supabase
      .from('risks')
      .update(updateData)
      .eq('id', riskId)
      .select()
      .single();

    if (error) handleError(error, 'update risk');
    if (!data) throw new Error('No data returned from update');
    return data;
  } catch (error) {
    handleError(error, 'update risk');
    throw error;
  }
};

/**
 * Delete a risk
 */
export const deleteRisk = async (riskId: string): Promise<void> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('risks')
      .delete()
      .eq('id', riskId);

    if (error) handleError(error, 'delete risk');
  } catch (error) {
    handleError(error, 'delete risk');
    throw error;
  }
};
